import * as React from "react";
import { cva } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  Loader2,
} from "lucide-react";

import { cn } from "../../lib/utils";
import { Separator } from "./separator";
import { Button } from "./button";
import { Badge } from "./badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";

const multiSelectVariants = cva(
  "mx-1",
  {
    variants: {
      variant: {
        default: "",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export const MultiSelect = React.forwardRef(
  (
    {
      options,
      onValueChange,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      maxSelections,
      modalPopover = false,
      asChild = false,
      className,
      variant,
      hideSelectAll = false,
      showSearch = true,
      onSearchChange,
      isLoading = false,
      debounceMs = 300,
      type = "object", // New prop: "object" or "string"
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState("");
    const searchTimeoutRef = React.useRef(null);
    const triggerRef = React.useRef(null);

    // Normalize options to always work with object format internally
    const normalizedOptions = React.useMemo(() => {
      // Safety check: ensure options is a valid array
      if (!options || !Array.isArray(options)) {
        return [];
      }
      
      // Convert string array to object array if type is "string"
      if (type === "string") {
        return options.map(opt => 
          typeof opt === "string" 
            ? { value: opt, label: opt }
            : opt
        );
      }
      
      // Return options as-is for object type
      return options;
    }, [options, type]);

    // Check if options are grouped
    const isGrouped = normalizedOptions.length > 0 && normalizedOptions[0]?.heading !== undefined;

    // Flatten options if grouped and ensure selected values are always included
    const flatOptions = React.useMemo(() => {
      let baseOptions = [];
      if (isGrouped) {
        baseOptions = normalizedOptions.flatMap((group) => group.options || []);
      } else {
        baseOptions = normalizedOptions;
      }

      // Create a Set of option values for quick lookup
      const optionValues = new Set(baseOptions.map(opt => opt.value));
      
      // Find selected values that are not in current options
      const missingSelectedOptions = selectedValues
        .filter(val => !optionValues.has(val))
        .map(val => ({
          value: val,
          label: val,
          // Mark as selected so they appear in the list
          _isPreselected: true
        }));

      // Combine current options with missing selected options
      return [...missingSelectedOptions, ...baseOptions];
    }, [normalizedOptions, isGrouped, selectedValues]);

    React.useImperativeHandle(ref, () => ({
      clear: () => {
        setSelectedValues([]);
        onValueChange([]);
      },
      getSelectedValues: () => selectedValues,
    }));

    // Debounced search handler
    const handleSearchChange = (value) => {
      setSearchValue(value);
      
      if (onSearchChange) {
        // Clear existing timeout
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debounced search
        searchTimeoutRef.current = setTimeout(() => {
          onSearchChange(value);
        }, debounceMs);
      }
    };

    // Add this useEffect after the existing useEffects in MultiSelect component
    React.useEffect(() => {
      if (props.value !== undefined) {
        setSelectedValues(props.value);
      }
    }, [props.value]);

    // Cleanup timeout on unmount
    React.useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    const handleInputKeyDown = (event) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (value) => {
      const isCurrentlySelected = selectedValues.includes(value);
      
      // If deselecting, always allow
      if (isCurrentlySelected) {
        const newSelectedValues = selectedValues.filter((v) => v !== value);
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
        return;
      }
      
      // If selecting, check maxSelections limit
      if (maxSelections && selectedValues.length >= maxSelections) {
        // Don't allow selection - optionally show feedback
        return;
      }
      
      const newSelectedValues = [...selectedValues, value];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const handleSelectAll = () => {
      const allValues = flatOptions
        .filter((option) => !option.disabled)
        .map((option) => option.value);
      
      // If maxSelections is set, limit to that number
      const valuesToSelect = maxSelections 
        ? allValues.slice(0, maxSelections)
        : allValues;
      
      setSelectedValues(valuesToSelect);
      onValueChange(valuesToSelect);
    };

    const toggleAll = () => {
      if (selectedValues.length === flatOptions.length) {
        handleClear();
      } else {
        handleSelectAll();
      }
    };

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={(node) => {
              triggerRef.current = node;
              if (typeof ref === 'function') {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            {...props}
            onClick={handleTogglePopover}
            className={cn(
              "flex w-full p-1 rounded-md border min-h-9 h-auto items-center justify-between bg-inherit hover:bg-inherit [&:hover]:bg-inherit",
              selectedValues.length > 0 && "h-auto py-0.5",
              className
            )}
            style={{ height: 'auto' }}
          >
            {selectedValues.length > 0 ? (
              <div className="flex justify-between items-start w-full gap-2 py-0.5">
                <div className="flex items-center gap-1 flex-wrap flex-1 min-w-0 my-0.5">
                  {selectedValues.map((value) => {
                    const option = flatOptions.find((o) => o.value === value);
                    const IconComponent = option?.icon;
                    return (
                      <Badge
                        key={value}
                        variant="accent"
                        className={cn(
                          "flex items-center gap-1 px-2 py-0.5 flex-shrink-0",
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant })
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {IconComponent && (
                          <IconComponent className="h-3 w-3" />
                        )}
                        <span className="text-xs whitespace-nowrap">{option?.label}</span>
                        <XCircle
                          className="h-3 w-3 cursor-pointer hover:text-destructive flex-shrink-0"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                  <XIcon
                    className="h-4 w-4 cursor-pointer text-muted-foreground hover:text-foreground"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleClear();
                    }}
                  />
                  <Separator
                    orientation="vertical"
                    className="h-4"
                  />
                  <ChevronDown className="h-4 w-4 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          style={{
            width: triggerRef.current?.offsetWidth || 'auto'
          }}
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command shouldFilter={false}>
            {showSearch && (
              <CommandInput
                placeholder="Search..."
                value={searchValue}
                onValueChange={handleSearchChange}
                onKeyDown={handleInputKeyDown}
              />
            )}
            <CommandList
              onWheel={(e) => {
                e.stopPropagation();
                const target = e.currentTarget;
                target.scrollTop += e.deltaY;
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No results found.</CommandEmpty>
                  
                  {/* Hide "Select All" when maxSelections is set */}
                  {!hideSelectAll && !maxSelections && flatOptions.length > 0 && (
                    <>
                      <CommandGroup>
                        <CommandItem
                          key="all"
                          onSelect={toggleAll}
                          className="cursor-pointer"
                        >
                          <div
                            className={cn(
                              "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                              selectedValues.length === flatOptions.length
                                ? "bg-primary text-primary-foreground"
                                : "opacity-50 [&_svg]:invisible"
                            )}
                          >
                            <CheckIcon className="h-4 w-4" />
                          </div>
                          <span>(Select All)</span>
                        </CommandItem>
                      </CommandGroup>
                      <CommandSeparator />
                    </>
                  )}
                  
                  {isGrouped ? (
                    normalizedOptions.map((group) => (
                      <CommandGroup key={group.heading} heading={group.heading}>
                        {group.options.map((option) => {
                          const isSelected = selectedValues.includes(option.value);
                          const isDisabled = option.disabled || 
                            (maxSelections && !isSelected && selectedValues.length >= maxSelections);
                          
                          return (
                            <CommandItem
                              key={option.value}
                              onSelect={() => toggleOption(option.value)}
                              className={cn(
                                "cursor-pointer",
                                isDisabled && "opacity-50 cursor-not-allowed"
                              )}
                              disabled={isDisabled}
                            >
                              <div
                                className={cn(
                                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible"
                                )}
                              >
                                <CheckIcon className="h-4 w-4" />
                              </div>
                              {option.icon && (
                                <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                              )}
                              <span>{option.label}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    ))
                  ) : (
                    <CommandGroup>
                      {flatOptions.map((option) => {
                        const isSelected = selectedValues.includes(option.value);
                        const isDisabled = option.disabled || 
                          (maxSelections && !isSelected && selectedValues.length >= maxSelections);
                        
                        return (
                          <CommandItem
                            key={option.value}
                            onSelect={() => toggleOption(option.value)}
                            className={cn(
                              "cursor-pointer",
                              isDisabled && "opacity-50 cursor-not-allowed"
                            )}
                            disabled={isDisabled}
                          >
                            <div
                              className={cn(
                                "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50 [&_svg]:invisible"
                              )}
                            >
                              <CheckIcon className="h-4 w-4" />
                            </div>
                            {option.icon && (
                              <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                            )}
                            <span>{option.label}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

MultiSelect.displayName = "MultiSelect";